import { useMemo } from 'react';
import { Check, Truck, Banknote, AlertTriangle, Lock } from 'lucide-react';
import SheetModal, { SheetNadpisSkupiny } from '../../components/SheetModal';

const fmtCislo = (n) => (n ?? null) === null ? '—' : Number(n).toLocaleString('cs-CZ');

/**
 * Výběr slotu (auto / pokladna) — standardní sheet aplikace.
 *
 * Dřív to bylo celostránkové překrytí s vlastní hlavičkou, šipkou zpět a
 * krokovou pilulkou: jedno klepnutí uprostřed přípravy směny se tvářilo jako
 * samostatná obrazovka a člověk ztratil, kde ve směně je. Pilulka navíc
 * předstírala průvodce, který tu není — výměna auta za běhu žádné pořadí
 * kroků nemá a musela si do ní podstrčit „⇄" místo čísla.
 *
 * Klik na položku = onSelect(id) + zavření. Items dostane už serverem označené
 * `obsazene` / `je_moje` / `naposled_moje`.
 */
export default function SlotPickerSheet({
    kind,                 // 'auto' | 'pokladna'
    items,                // pole z dostupna-vozidla / dostupne-pokladny
    selectedId,           // aktuálně vybrané ID (zvýrazněno)
    onSelect,             // (id) => void
    onClose,              // () => void
    prohodit = false,     // auto kolegy na běžící směně jde prohodit (mám svoje)
    isDark,
}) {
    const isAuto = kind === 'auto';

    const { volne, obsazene, zamcene } = useMemo(() => {
        const all = items || [];
        return {
            volne: all.filter(i => (!i.obsazene && !i.manazer_only) || i.je_moje),
            obsazene: all.filter(i => i.obsazene && !i.je_moje),
            zamcene: all.filter(i => i.manazer_only && !i.obsazene && !i.je_moje),
        };
    }, [items]);

    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const prazdno = !volne.length && !obsazene.length && !zamcene.length;

    return (
        <SheetModal
            onClose={onClose}
            isDark={isDark}
            ikona={isAuto ? Truck : Banknote}
            // Stejný akcent, jaký nese dlaždice slotu na kartě směny — modrá
            // auto, zelená pokladna. Sheet tak vypadá jako její pokračování.
            barva={isAuto ? '#3B82F6' : '#10B981'}
            nadpis={isAuto ? 'Vyber auto' : 'Vyber pokladnu'}
        >
            {prazdno && (
                <div style={{ textAlign: 'center', color: textMuted, padding: 32, fontSize: 13 }}>
                    Žádná dostupná položka.
                </div>
            )}

            {volne.map(item => (
                <PickerCard key={item.id}
                    kind={kind}
                    item={item}
                    selected={selectedId === item.id}
                    onClick={() => { onSelect?.(item.id); onClose?.(); }}
                    isDark={isDark}
                />
            ))}

            {obsazene.length > 0 && (
                <>
                    <SheetNadpisSkupiny isDark={isDark}>Obsazené</SheetNadpisSkupiny>
                    {obsazene.map(item => (prohodit && item.lze_prohodit ? (
                        <PickerCard key={item.id} kind={kind} item={item} prohodit
                            onClick={() => { onSelect?.(item.id); onClose?.(); }}
                            isDark={isDark} />
                    ) : (
                        <PickerCard key={item.id} kind={kind} item={item} disabled isDark={isDark} />
                    )))}
                </>
            )}

            {zamcene.length > 0 && (
                <>
                    <SheetNadpisSkupiny isDark={isDark}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Lock size={11} /> Jen manažer směny
                        </span>
                    </SheetNadpisSkupiny>
                    {zamcene.map(item => (
                        <PickerCard key={item.id} kind={kind} item={item} disabled isDark={isDark} />
                    ))}
                </>
            )}
        </SheetModal>
    );
}

function PickerCard({ kind, item, selected, disabled, prohodit = false, onClick, isDark }) {
    const isAuto = kind === 'auto';
    const lowFuel = isAuto && typeof item.palivo_procent === 'number' && item.palivo_procent < 20;

    const baseBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const baseBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';

    let bg = baseBg;
    let border = baseBorder;
    let leftIcon = isAuto ? <Truck size={20} /> : <Banknote size={20} />;
    let leftIconBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
    let leftIconColor = textMuted;

    if (selected) {
        bg = 'rgba(16,185,129,0.10)';
        border = 'rgba(16,185,129,0.45)';
        leftIcon = <Check size={20} />;
        leftIconBg = 'rgba(16,185,129,0.25)';
        leftIconColor = '#10B981';
    } else if (lowFuel) {
        bg = 'rgba(239,68,68,0.10)';
        border = 'rgba(239,68,68,0.40)';
        leftIcon = <AlertTriangle size={20} />;
        leftIconBg = 'rgba(239,68,68,0.20)';
        leftIconColor = '#EF4444';
    }

    if (disabled) {
        bg = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)';
        border = baseBorder;
        leftIconColor = textMuted;
    }

    return (
        <button type="button"
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: 12,
                background: bg, border: `1px solid ${border}`,
                borderRadius: 10,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                textAlign: 'left',
                color: textPrimary,
            }}>
            <span style={{
                width: 38, height: 38, borderRadius: 7, flexShrink: 0,
                background: leftIconBg, color: leftIconColor,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>{leftIcon}</span>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {isAuto ? item.spz : item.nazev}
                    </span>
                    {prohodit && (
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                            padding: '2px 7px', borderRadius: 999,
                            background: 'rgba(59,130,246,0.18)', color: '#3B82F6',
                            border: '1px solid rgba(59,130,246,0.35)',
                        }}>Prohodit</span>
                    )}
                    {item.naposled_moje && !disabled && (
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                            padding: '2px 7px', borderRadius: 999,
                            background: 'rgba(16,185,129,0.18)', color: '#10B981',
                            border: '1px solid rgba(16,185,129,0.35)',
                        }}>Naposled</span>
                    )}
                </div>
                <div style={{ fontSize: 12, color: lowFuel ? '#EF4444' : textMuted, marginTop: 2 }}>
                    {isAuto ? (
                        <>
                            {item.model || '—'}
                            {typeof item.km_stav === 'number' && (
                                <> · {fmtCislo(item.km_stav)} km</>
                            )}
                            {item.ma_traccar && <> · 🛰️</>}
                            {(disabled || prohodit) && item.obsazene_kym && <> · {prohodit ? `má ${item.obsazene_kym}, dostane tvoje` : item.obsazene_kym}</>}
                        </>
                    ) : (
                        <>
                            {item.typ === 'rozvoz' ? 'Rozvoz' : 'Bistro'}
                            {item.pocatecni_castka_kc > 0 && (
                                <> · vklad {fmtCislo(item.pocatecni_castka_kc)} Kč</>
                            )}
                            {disabled && item.obsazene_kym && <> · {item.obsazene_kym}</>}
                            {disabled && item.manazer_only && !item.obsazene_kym && <> · jen manažer směny</>}
                        </>
                    )}
                </div>
            </div>
        </button>
    );
}
